import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { LoadingModal } from "../../../../components/LoadingModal";
import { PlusTag } from "../../../../components/PlusTag";
import { useQueryUrl } from "../../../../hooks/QueryUrl";
import ChevronLeftIcon from "../../../../icons/ChevronLeft";
import { api, Renewal } from "../../../../services/api";
import { isApiError } from "../../../../services/api/errors";
import { formatDate as formatLocaleDate, formatDateTime } from "../../../../services/i18ln/format";
import { formatMoney } from "../../../../utils/money";
import { BoletoIcon, PixIcon } from "../Checkout/icons";
import { TopLinks } from "../Features/styles";
import { formatBoletoLine } from "../PaymentPending/pendingPayment";
import { Card, Code, Container, Content, QrCode, Status } from "../PaymentPending/styles";
import { SwitchToCard } from "./styles";

// While the page is open, the payment is checked this often (it starts a new year once it arrives)
const POLL_MS = 5000;

/**
 * Renewing a year of PLUS paid once with Pix or boleto (reached from the renewal emails): the boleto or a fresh
 * Pix to pay, and the way to switch to a card that renews by itself.
 */
export const RenewPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryUrl = useQueryUrl();
  const prefix = "pages.subscription.renew";
  const siteId = queryUrl.get("site") || "";

  const [data, setData] = useState<Renewal>();
  const [failure, setFailure] = useState<"notAvailable" | "generic">();
  const [renewedUntil, setRenewedUntil] = useState<string>();
  const [copied, setCopied] = useState(false);

  const formatDate = (iso?: string) => (iso ? formatLocaleDate(iso, i18n.language) : "");
  const formatTime = (iso?: string) => (iso ? formatDateTime(iso, i18n.language) : "");

  useEffect(() => {
    if (!siteId) {
      navigate("/sites", { replace: true });
      return;
    }
    api
      .getRenewal(siteId)
      .then(setData)
      .catch(error => setFailure(isApiError(error, "RENEWAL_NOT_AVAILABLE") ? "notAvailable" : "generic"));
  }, [navigate, siteId]);

  // Paid: the PLUS gets a new end date (a year from the payment)
  useEffect(() => {
    if (!data || renewedUntil) {
      return;
    }
    const timer = window.setInterval(async () => {
      api.clearCacheSites();
      const sites = await api.getSites().catch(() => []);
      const periodEnd = sites.find(item => item.siteId === siteId)?.subscription?.currentPeriodEnd;
      if (periodEnd && dayjs(periodEnd).isAfter(dayjs(data.currentPeriodEnd))) {
        setRenewedUntil(periodEnd);
      }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [data, renewedUntil, siteId]);

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  if (!data && !failure) {
    return <LoadingModal />;
  }

  const renewal = data?.renewal;
  const method = renewal?.method || "pix";

  return (
    <Container>
      <TopLinks>
        <button type="button" onClick={() => navigate("/sites")}>
          <ChevronLeftIcon width={10} height={10} color="currentColor" />
          {t(`${prefix}.back`)}
        </button>
      </TopLinks>

      <Content>
        {failure || !renewal ? (
          <header>
            <h1>{t(`${prefix}.${failure === "notAvailable" ? "notAvailable" : "error"}.title`)}</h1>
            <p>{t(`${prefix}.${failure === "notAvailable" ? "notAvailable" : "error"}.text`)}</p>
          </header>
        ) : renewedUntil ? (
          <header>
            <span className="method-icon">✦</span>
            <h1>{t(`${prefix}.done.title`)}</h1>
            <p>{t(`${prefix}.done.text`, { date: formatDate(renewedUntil) })}</p>
          </header>
        ) : (
          <>
            <header>
              <span className="method-icon">
                {method === "pix" ? <PixIcon width={28} height={28} /> : <BoletoIcon width={28} height={28} />}
              </span>
              <h1>{t(`${prefix}.title`)}</h1>
              <p>{t(`${prefix}.subtitle`, { date: formatDate(data?.currentPeriodEnd) })}</p>
            </header>

            <Card>
              <div className="amount">
                <PlusTag>✦ Keepo Plus</PlusTag>
                <b>
                  {formatMoney(renewal.amount, "brl", i18n.language)}
                  <small>{t("pages.subscription.features.plans.perYear")}</small>
                </b>
              </div>

              {method === "pix" ? (
                <>
                  <QrCode $expired={false}>
                    {renewal.pix?.qrImageUrl && <img src={renewal.pix.qrImageUrl} alt="QR code Pix" />}
                  </QrCode>
                  <p className="timer">{t(`${prefix}.pixValidUntil`, { time: formatTime(renewal.pix?.expiresAt) })}</p>
                  <Code>
                    <span className="label">{t("pages.subscription.pending.pix.copyPaste")}</span>
                    <code>{renewal.pix?.code}</code>
                  </Code>
                  <button type="button" className="primary" onClick={() => copy(renewal.pix?.code || "")}>
                    {copied ? t("pages.subscription.pending.copied") : t("pages.subscription.pending.pix.copy")}
                  </button>
                </>
              ) : (
                <>
                  <p className="due">
                    {t("pages.subscription.pending.boleto.dueOn", { date: formatDate(renewal.boleto?.expiresAt) })}
                  </p>
                  <Code>
                    <span className="label">{t("pages.subscription.pending.boleto.line")}</span>
                    <code className="boleto-line">{formatBoletoLine(renewal.boleto?.number || "")}</code>
                  </Code>
                  <div className="actions">
                    <button
                      type="button"
                      className="primary"
                      onClick={() => copy((renewal.boleto?.number || "").replace(/\D/g, ""))}>
                      {copied ? t("pages.subscription.pending.copied") : t("pages.subscription.pending.boleto.copy")}
                    </button>
                    <a
                      className="secondary"
                      href={renewal.boleto?.pdfUrl || renewal.boleto?.hostedUrl}
                      target="_blank"
                      rel="noreferrer">
                      {t("pages.subscription.pending.boleto.download")}
                    </a>
                  </div>
                </>
              )}
            </Card>

            <Status $calm={method === "boleto"}>
              <span className="dot" aria-hidden="true" />
              <span className="text">
                <b>{t(`${prefix}.waiting`)}</b>
                <span>{t(`${prefix}.waitingText.${method}`)}</span>
              </span>
            </Status>

            {/* The way out of renewing by hand every year */}
            <SwitchToCard>
              <span className="text">
                <b>{t(`${prefix}.card.title`)}</b>
                <span>{t(`${prefix}.card.text`)}</span>
              </span>
              <button
                type="button"
                onClick={() => navigate(`/subscription/to-card?site=${encodeURIComponent(siteId)}`)}>
                {t(`${prefix}.card.button`)}
              </button>
            </SwitchToCard>
          </>
        )}
      </Content>
    </Container>
  );
};
